/**
 * RoleAccessAdminTab — admin toggles which dashboard tabs operators and
 * cashiers see. Reads/writes /api/admin/role-dashboards.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Loader2, Save, Lock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const OPERATOR_TABS = [
    { key: 'overview',     label: 'Overview',     desc: 'Daily KPIs and the weekly settlement card.' },
    { key: 'players',      label: 'Players',      desc: 'Full location roster, search, activity drill, load & cashout.' },
    { key: 'cashouts',     label: 'Cashouts',     desc: 'Pending player cashout requests for their store.' },
    { key: 'lottery',      label: 'Place Ticket', desc: 'Place a Pick 3/4 or Big 3 lottery ticket on behalf of a player.' },
    { key: 'transactions', label: 'Transactions', desc: 'Every txn in the location, filterable by date / type.' },
    { key: 'reports',      label: 'Reports',      desc: 'Sports / Lottery / Slots financial breakdown + weekly settlement.' },
    { key: 'profit',       label: 'Profit',       desc: "Operator-only daily + weekly profit and the cash each cashier owes the operator (with Mark Paid)." },
    { key: 'cashiers',     label: 'Cashiers',     desc: 'Add / edit / disable storefront staff.' },
    { key: 'settings',     label: 'Settings',     desc: 'Toggle this location\'s feature flags (sportsbook, casino, lottery states, etc.).' },
];

const CASHIER_TABS = [
    { key: 'today',        label: 'Today',        desc: 'Daily report tiles for the cashier.' },
    { key: 'players',      label: 'My players',   desc: 'Their owned + unclaimed players; create new.' },
    { key: 'cashouts',     label: 'Cashouts',     desc: 'Pending cashouts for their players (needs can_cashout).' },
    { key: 'lottery',      label: 'Place Ticket', desc: 'Place a Pick 3/4 or Big 3 lottery ticket on behalf of one of their players.' },
    { key: 'transactions', label: 'Transactions', desc: 'Every txn for their sub-group, filterable.' },
    { key: 'reports',      label: 'Reports',      desc: 'Sports / Lottery / Slots breakdown + weekly card.' },
];

const RoleAccessAdminTab = () => {
    const [data, setData] = useState({ operator: [], cashier: [] });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/role-dashboards`, { withCredentials: true });
            setData({ operator: data.operator || [], cashier: data.cashier || [] });
            setDirty(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to load');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const toggle = (role, key) => {
        setData(prev => {
            const list = prev[role] || [];
            const next = list.includes(key) ? list.filter(k => k !== key) : [...list, key];
            return { ...prev, [role]: next };
        });
        setDirty(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/admin/role-dashboards`, data, { withCredentials: true });
            toast.success('Role access updated');
            setDirty(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Save failed');
        } finally { setSaving(false); }
    };

    if (loading) {
        return (
            <div className="text-center py-12 text-zinc-500">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
            </div>
        );
    }

    return (
        <div className="space-y-5" data-testid="role-access-tab">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                        <Lock className="w-4 h-4" /> Role access
                    </h2>
                    <p className="text-xs text-[#A0AAB2] mt-0.5">
                        Choose which dashboard tabs operators and cashiers can see. Visibility rules
                        (cashier sub-groups, location scoping) are always enforced server-side — this
                        only hides UI sections.
                    </p>
                </div>
                <Button onClick={save} disabled={!dirty || saving}
                    className="bg-[#FFD400] text-black font-bold hover:bg-[#FFD400]/90"
                    data-testid="role-access-save">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}
                </Button>
            </div>

            <RolePanel
                role="operator" label="Operator dashboard"
                tabs={OPERATOR_TABS} active={data.operator || []} onToggle={toggle}
            />
            <RolePanel
                role="cashier" label="Cashier dashboard"
                tabs={CASHIER_TABS} active={data.cashier || []} onToggle={toggle}
            />
        </div>
    );
};

const RolePanel = ({ role, label, tabs, active, onToggle }) => (
    <div className="card-dark p-5" data-testid={`role-panel-${role}`}>
        <h3 className="text-sm font-semibold text-white mb-1">{label}</h3>
        <p className="text-xs text-[#A0AAB2] mb-4">
            {active.length} of {tabs.length} sections visible
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {tabs.map(t => {
                const on = active.includes(t.key);
                return (
                    <button key={t.key} type="button" onClick={() => onToggle(role, t.key)}
                        className={`text-left flex items-start gap-3 p-3 rounded-md border transition-colors
                            ${on
                                ? 'bg-[#FFD400]/10 border-[#FFD400]/40'
                                : 'bg-white/[0.03] border-white/10 hover:border-white/20'}`}
                        data-testid={`toggle-${role}-${t.key}`}>
                        <div className={`mt-0.5 w-9 h-5 rounded-full flex-shrink-0 relative transition-colors
                            ${on ? 'bg-[#FFD400]' : 'bg-white/10'}`}>
                            <span className={`absolute top-0.5 ${on ? 'right-0.5' : 'left-0.5'} w-4 h-4 bg-white rounded-full transition-all`} />
                        </div>
                        <div className="min-w-0">
                            <p className="text-sm font-medium text-white">{t.label}</p>
                            <p className="text-xs text-[#A0AAB2]">{t.desc}</p>
                        </div>
                    </button>
                );
            })}
        </div>
    </div>
);

export default RoleAccessAdminTab;
